import { useEffect, useRef, useCallback } from 'react';
/**
 * Custom hook to detect device shake gestures using DeviceMotionEvent API
 * Works on mobile devices with accelerometer support
 *
 * @param options Configuration options
 * @returns cleanup function (automatically called on unmount)
 */
export function useShakeDetection({ threshold = 25, cooldownMs = 500, onShake, }) {
    const lastShakeTimeRef = useRef(0);
    const previousAccelerationRef = useRef({
        x: 0,
        y: 0,
        z: 0,
    });
    const handleDeviceMotion = useCallback((event) => {
        const acceleration = event.acceleration;
        if (!acceleration)
            return;
        // Calculate the change in acceleration
        const deltaX = Math.abs((acceleration.x || 0) - previousAccelerationRef.current.x);
        const deltaY = Math.abs((acceleration.y || 0) - previousAccelerationRef.current.y);
        const deltaZ = Math.abs((acceleration.z || 0) - previousAccelerationRef.current.z);
        previousAccelerationRef.current = {
            x: acceleration.x || 0,
            y: acceleration.y || 0,
            z: acceleration.z || 0,
        };
        // Check if the total change exceeds the threshold
        const totalDelta = deltaX + deltaY + deltaZ;
        if (totalDelta > threshold) {
            const now = Date.now();
            // Only trigger if enough time has passed since the last shake
            if (now - lastShakeTimeRef.current > cooldownMs) {
                lastShakeTimeRef.current = now;
                onShake();
            }
        }
    }, [threshold, cooldownMs, onShake]);
    useEffect(() => {
        // Check if DeviceMotionEvent is supported
        if (typeof window === 'undefined' || !window.DeviceMotionEvent) {
            return;
        }
        // Request permission for iOS 13+
        if (typeof DeviceMotionEvent?.requestPermission === 'function') {
            DeviceMotionEvent
                .requestPermission()
                .then((permission) => {
                if (permission === 'granted') {
                    window.addEventListener('devicemotion', handleDeviceMotion);
                }
            })
                .catch(() => {
                // Permission denied, skip shake detection
            });
        }
        else {
            // Non-iOS 13+ devices don't require permission
            window.addEventListener('devicemotion', handleDeviceMotion);
        }
        return () => {
            window.removeEventListener('devicemotion', handleDeviceMotion);
        };
    }, [handleDeviceMotion]);
}
